import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatErr } from "../../api";
import { SectionTitle, StatusPill, EmptyState, RichHtml } from "../../components/Atoms";
import CreatorSubsTable from "../../components/CreatorSubsTable";
import {
  ArrowLeft, Calendar, Funnel,
  Users, ListChecks, ClipboardText, Hourglass,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const GENDER_OPTIONS = ["All", "Male", "Female", "Prefer not to say"];

export default function ClientTaskDetailPage() {
  const { pid, tid } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [task, setTask] = useState(null);
  const [ambassadors, setAmbassadors] = useState([]);   // all approved creators on the project
  const [submissions, setSubmissions] = useState([]);
  const [feedback, setFeedback] = useState({});
  const [filters, setFilters] = useState({ status: "All", creator: "all", gender: "All" });

  const load = useCallback(async () => {
    const [p, a, s] = await Promise.all([
      api.get(`/projects/${pid}`),
      api.get(`/projects/${pid}/ambassadors`),
      api.get(`/submissions?project_id=${pid}&task_id=${tid}`),
    ]);
    setProject(p.data);
    setTask((p.data.tasks || []).find(t => t.id === tid) || null);
    setAmbassadors(a.data);
    setSubmissions(s.data);
  }, [pid, tid]);
  useEffect(() => { load(); }, [load]);

  // Build one row per ASSIGNED creator (every approved creator is assigned to every task).
  // If they have submissions, attach them; otherwise show a single "Not started" placeholder row.
  const allRows = useMemo(() => {
    const subsByAmb = {};
    submissions.forEach(s => { (subsByAmb[s.ambassador_id] = subsByAmb[s.ambassador_id] || []).push(s); });
    return ambassadors.map(amb => ({
      amb,
      subs: subsByAmb[amb.id] || [],
    }));
  }, [ambassadors, submissions]);

  // Summary cards driven by actual assignment data
  const summary = useMemo(() => {
    const totalCreators = ambassadors.length;
    const totalAssignments = totalCreators; // 1 per creator-task pair
    let completed = 0;
    let pending = 0;
    allRows.forEach(({ subs }) => {
      // Status priority: any Approved => Completed; any Pending OR no submission yet => Pending; else Rejected (not counted in Pending)
      const hasApproved = subs.some(s => s.status === "Approved");
      const hasPending  = subs.some(s => s.status === "Pending");
      const notStarted  = subs.length === 0;
      if (hasApproved) completed += 1;
      else if (hasPending || notStarted) pending += 1;
    });
    return { totalCreators, totalAssignments, completed, pending };
  }, [ambassadors, allRows]);

  // Apply filters for the table view
  const rows = useMemo(() => {
    let out = allRows;
    if (filters.creator !== "all") out = out.filter(r => r.amb.id === filters.creator);
    if (filters.gender !== "All")  out = out.filter(r => (r.amb.gender || "") === filters.gender);
    if (filters.status !== "All") {
      out = out.filter(r => {
        if (filters.status === "Not started") return r.subs.length === 0;
        return r.subs.some(s => s.status === filters.status);
      });
    }
    return out;
  }, [allRows, filters]);

  const act = async (sid, action) => {
    try {
      await api.post(`/submissions/${sid}/${action}`, { feedback: feedback[sid] || "" });
      toast.success(`Submission ${action}d`); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (!project || !task) return <div className="skeleton h-64" />;

  return (
    <div className="space-y-5">
      <button onClick={() => nav(`/client/projects/${pid}`)} className="btn-ghost flex items-center gap-1.5 text-sm" data-testid="back-to-project">
        <ArrowLeft size={14} /> Back to project
      </button>

      {/* Task header + full description */}
      <div className="glass p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-muted font-bold">
              <Link to={`/client/projects/${pid}`} className="hover:text-[#22D3EE]">{project.title}</Link> · Task
            </div>
            <div className="text-white text-2xl font-bold mt-1" data-testid="task-title">{task.title}</div>
            <div className="flex items-center gap-2 flex-wrap mt-2">
              {((task.platforms && task.platforms.length > 0) ? task.platforms : (task.platform ? [task.platform] : [])).map(pl => (
                <span key={pl} className="text-[10px] uppercase tracking-wider font-bold text-[#22D3EE] bg-[#22D3EE]/10 border border-[#22D3EE]/30 px-2 py-0.5 rounded-full whitespace-nowrap">{pl}</span>
              ))}
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#A3E635] bg-[#A3E635]/10 border border-[#A3E635]/30 px-2 py-0.5 rounded-full">{fmtMoney((task.reward_amount||0)+(task.bonus_amount||0))}</span>
              {task.deadline && <span className="text-xs text-muted flex items-center gap-1"><Calendar size={12} /> {fmtDate(task.deadline)}</span>}
              <StatusPill status={task.status || "Active"} />
            </div>
            {(task.description || task.instructions) && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-muted font-bold mb-1">Task description</div>
                <RichHtml html={task.description || task.instructions} className="text-sm text-white/90 max-w-3xl rich-html" />
              </div>
            )}
            {task.instructions && task.description && task.instructions !== task.description && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-muted font-bold mb-1">Instructions</div>
                <RichHtml html={task.instructions} className="text-sm text-white/90 max-w-3xl rich-html" />
              </div>
            )}
            {task.proof_required && (
              <div className="text-xs text-muted mt-3">
                Proof required: <span className="text-white">{task.proof_required}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="task-summary-cards">
        <Stat icon={Users}         label="Total Creators"        value={summary.totalCreators}    color="#22D3EE" testid="stat-total-creators" />
        <Stat icon={ListChecks}    label="Total Assignments"     value={summary.totalAssignments} color="#8B5CF6" testid="stat-total-assignments" />
        <Stat icon={ClipboardText} label="Completed Assignments" value={summary.completed}        color="#A3E635" testid="stat-completed" />
        <Stat icon={Hourglass}     label="Pending Assignments"   value={summary.pending}          color="#F59E0B" testid="stat-pending" />
      </div>

      {/* Filters */}
      <div className="glass p-3 flex flex-wrap gap-2 items-center" data-testid="task-page-filters">
        <div className="flex items-center gap-1.5 text-xs uppercase font-bold text-muted tracking-wider"><Funnel size={14} weight="bold" /> Filter creators</div>
        <select value={filters.creator} onChange={e => setFilters({...filters, creator:e.target.value})} className="input-base text-sm py-1.5 max-w-[220px]" data-testid="task-filter-creator">
          <option value="all">All creators</option>
          {ambassadors.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={filters.gender} onChange={e => setFilters({...filters, gender:e.target.value})} className="input-base text-sm py-1.5 max-w-[180px]" data-testid="task-filter-gender">
          {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g === "All" ? "All genders" : g}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status:e.target.value})} className="input-base text-sm py-1.5 max-w-[180px]" data-testid="task-filter-status">
          {["All","Pending","Approved","Rejected","Not started"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Creators table — every assigned creator gets a row. Reuses shared CreatorSubsTable. */}
      <div className="glass overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <div className="text-white font-bold" style={{ fontSize: "21px" }} data-testid="task-creators-heading">
            Creators assigned to this task ({rows.length})
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-5"><EmptyState title="No creators match these filters" /></div>
        ) : (
          <CreatorSubsTable rows={rows} feedback={feedback} setFeedback={setFeedback} onAct={act} />
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, color, testid }) => (
  <div className="p-4 rounded-2xl bg-white/5 border border-white/5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted font-bold flex items-center gap-1">
      <Icon size={12} weight="bold" style={{ color }} /> {label}
    </div>
    <div className="text-3xl font-black mt-1" style={{ color }}>{value}</div>
  </div>
);
